// A boat's notebook as an admin reads it (TOM-616, ADR 0370): the shape
// GET /v1/admin/boats/:boatId/notebook answers with, and the plain words the
// page gives each part of the boat, status and kind. Kept apart from the page
// so the words are tested without a DOM (notebook.test.ts).

export type NotebookArea = 'boards' | 'electrical' | 'mechanical' | 'electronics' | 'general';
export type NotebookKind = 'identity' | 'wiring' | 'rating' | 'normal_range' | 'habit' | 'quirk';
export type NotebookStatus = 'active' | 'contradicted' | 'stale' | 'set_aside' | 'forgotten';

export interface NotebookBasis {
  paths: string[];
  tools: string[];
  window: { from: string; to: string } | null;
}

/** One note. It names no account and not the part of Neo that wrote it. */
export interface NotebookNote {
  id: string;
  area: NotebookArea;
  subject: string | null;
  kind: NotebookKind;
  /** Null only for a forgotten note. */
  text: string | null;
  origin: 'observed' | 'owner';
  status: NotebookStatus;
  confidence: 'owner' | 'confirmed' | 'repeated' | 'once';
  confirmed: boolean;
  confirmedAt: string | null;
  basis: NotebookBasis | null;
  observedAt: string;
  lastCheckedAt: string;
  seenCount: number;
  disputedAt: string | null;
  dispute: NotebookBasis | null;
  authorIsFormerOwner: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface NotebookView {
  boat: { id: string; displayName: string; decommissioned: boolean };
  notes: NotebookNote[];
}

/** The parts of the boat, in the order the page shows them, as the owner's page names them. */
export const NOTEBOOK_AREAS: ReadonlyArray<{ area: NotebookArea; title: string }> = [
  { area: 'electrical', title: 'Batteries and charging' },
  { area: 'mechanical', title: 'Engines' },
  { area: 'electronics', title: 'Instruments and network' },
  { area: 'boards', title: 'Boards' },
  { area: 'general', title: 'General' },
];

const KIND_TEXT: Readonly<Record<NotebookKind, string>> = {
  identity: 'What it is',
  wiring: 'How it is wired',
  rating: 'Rating',
  normal_range: 'Normal range',
  habit: 'Habit',
  quirk: 'Quirk',
};

/** A status's words, and the colour of its badge (StatusBadge's vocabulary). */
const STATUS_TEXT: Readonly<Record<NotebookStatus, { label: string; badge: string }>> = {
  active: { label: 'In use', badge: 'published' },
  contradicted: { label: 'Contradicted by the readings', badge: 'rejected' },
  stale: { label: 'Its readings went quiet', badge: 'quiet' },
  set_aside: { label: "Set aside for an owner's note", badge: 'suggested' },
  forgotten: { label: 'Forgotten by an owner', badge: 'quiet' },
};

export function kindText(kind: NotebookKind): string {
  return KIND_TEXT[kind] ?? kind;
}

export function statusText(status: NotebookStatus): { label: string; badge: string } {
  return STATUS_TEXT[status] ?? { label: status, badge: 'quiet' };
}

/** A date without the time: "Oct 10, 2026". */
export function noteDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/** Where the note came from, never naming an owner. */
export function sourceText(note: NotebookNote): string {
  if (note.origin === 'observed') return 'Noticed in the readings';
  if (note.status === 'forgotten') return "An owner's note";
  return note.authorIsFormerOwner ? 'From a former owner' : 'From an owner';
}

/** How many times the readings showed an observed note. */
export function seenText(count: number): string {
  return count === 1 ? 'Seen once' : `Seen ${count} times`;
}

/** The notes of each part of the boat that has any, in page order. */
export function notesByArea(
  notes: readonly NotebookNote[]
): Array<{ area: NotebookArea; title: string; notes: NotebookNote[] }> {
  return NOTEBOOK_AREAS.map(({ area, title }) => ({
    area,
    title,
    notes: notes.filter((n) => n.area === area),
  })).filter((group) => group.notes.length > 0);
}

/**
 * What the page says when the notebook could not be read: a fixed line for
 * each answer, never the server's own message (a 500 would show a database
 * error).
 */
export function notebookErrorText(status: number | undefined, code: string | undefined): string {
  if (status === 400) {
    return code === 'reason_required'
      ? 'Give a reason of 3 to 200 characters, in plain words on one line.'
      : 'This link to a boat is not valid. Go back and choose the boat again.';
  }
  if (status === 401 || status === 403) {
    return 'Your admin sign-in has ended. Sign in again, then open the notebook.';
  }
  if (status === 404) return 'There is no such boat. It may have been erased.';
  if (status === 503) {
    return 'Your reading could not be logged, so the notebook stays closed. Try again in a moment.';
  }
  return 'The notebook could not be read. Try again in a moment.';
}

/** Where the page's back link goes: the page the admin came from, or Boats. */
export interface NotebookBack {
  to: string;
  label: string;
}

const BOATS_BACK: NotebookBack = { to: '/admin/boats', label: 'Boats' };

/**
 * The back link a page handed over in the link's state. Only a page inside the
 * admin console is taken, so the link never leads anywhere else.
 */
export function notebookBack(state: unknown): NotebookBack {
  const from = isRecord(state) && isRecord(state.from) ? state.from : null;
  if (
    from !== null &&
    typeof from.to === 'string' &&
    typeof from.label === 'string' &&
    ADMIN_PAGE.test(from.to) &&
    from.label.trim().length > 0
  ) {
    return { to: from.to, label: from.label };
  }
  return BOATS_BACK;
}

const ADMIN_PAGE = /^\/admin\/[A-Za-z0-9/_-]*$/;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** "9 notes: 6 in use, 2 went quiet, 1 forgotten". */
export function countsText(notes: readonly NotebookNote[]): string {
  if (notes.length === 0) return 'No notes';
  const total = `${notes.length} note${notes.length === 1 ? '' : 's'}`;
  const parts: string[] = [];
  const count = (status: NotebookStatus) => notes.filter((n) => n.status === status).length;
  const words: ReadonlyArray<[NotebookStatus, string]> = [
    ['active', 'in use'],
    ['contradicted', 'contradicted by the readings'],
    ['stale', 'went quiet'],
    ['set_aside', 'set aside'],
    ['forgotten', 'forgotten'],
  ];
  for (const [status, word] of words) {
    const n = count(status);
    if (n > 0) parts.push(`${n} ${word}`);
  }
  const disputed = notes.filter((n) => n.disputedAt !== null).length;
  if (disputed > 0) parts.push(`${disputed} disputed by the readings`);
  return `${total}: ${parts.join(', ')}`;
}
